/**
 * ConversationContext - React Context for conversation-specific state.
 *
 * Provides:
 * - Topic-based dialogue progress tracking
 * - Natural conversation flow management
 * - Voice/text mode switching
 *
 * The backend uses topic-based tracking with 4 required topics:
 * - current_values, current_challenges, future_hopes, technology_role
 * And 3 optional topics that may be covered naturally:
 * - current_strengths, technology_concerns, sustainability
 */

/* eslint-disable react-refresh/only-export-components -- Context exports hooks alongside provider */

import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react'

/**
 * Input mode for conversation
 */
export type InputMode = 'text' | 'voice'

/**
 * Topic category (matches backend QuestionCategory)
 */
export type TopicCategory =
  | 'current_values'
  | 'current_strengths'
  | 'current_challenges'
  | 'future_hopes'
  | 'future_timeframe'
  | 'technology_role'
  | 'technology_concerns'
  | 'sustainability'

/**
 * Required topics for dialogue completion
 */
export const REQUIRED_TOPICS: TopicCategory[] = [
  'current_values',
  'current_challenges',
  'future_hopes',
  'technology_role',
]

/**
 * Topic coverage from API response
 */
export interface TopicCoverage {
  coveredTopics: TopicCategory[]
  requiredCovered: number
  requiredTotal: number
  allRequiredMet: boolean
}

/**
 * Dialogue state
 */
export interface DialogueState {
  topicsCovered: TopicCategory[]
  requiredCovered: number
  requiredTotal: number
  isComplete: boolean
  inputMode: InputMode
}

/**
 * Conversation context state
 */
export interface ConversationContextState {
  dialogueState: DialogueState
  inputMode: InputMode
}

/**
 * Conversation context actions
 */
export interface ConversationContextActions {
  setInputMode: (mode: InputMode) => void
  updateTopicCoverage: (coverage: TopicCoverage) => void
  setDialogueComplete: () => void
  resetDialogue: () => void
}

/**
 * Conversation context value
 */
export interface ConversationContextValue
  extends ConversationContextState,
    ConversationContextActions {}

const ConversationContext = createContext<ConversationContextValue | undefined>(undefined)

/**
 * Conversation provider props
 */
export interface ConversationProviderProps {
  children: ReactNode
}

/**
 * Initial dialogue state
 */
const initialDialogueState: DialogueState = {
  topicsCovered: [],
  requiredCovered: 0,
  requiredTotal: REQUIRED_TOPICS.length,
  isComplete: false,
  inputMode: 'text',
}

/**
 * Conversation provider component
 */
export function ConversationProvider({ children }: ConversationProviderProps) {
  const [dialogueState, setDialogueState] = useState<DialogueState>(initialDialogueState)
  const [inputMode, setInputModeState] = useState<InputMode>('text')

  /**
   * Set input mode (text/voice)
   */
  const setInputMode = useCallback((mode: InputMode) => {
    setInputModeState(mode)
    setDialogueState((prev) => ({ ...prev, inputMode: mode }))
  }, [])

  /**
   * Update topic coverage from API response
   */
  const updateTopicCoverage = useCallback((coverage: TopicCoverage) => {
    setDialogueState((prev) => ({
      ...prev,
      topicsCovered: coverage.coveredTopics,
      requiredCovered: coverage.requiredCovered,
      requiredTotal: coverage.requiredTotal,
      isComplete: coverage.allRequiredMet,
    }))
  }, [])

  /**
   * Mark dialogue as complete
   */
  const setDialogueComplete = useCallback(() => {
    setDialogueState((prev) => ({
      ...prev,
      isComplete: true,
    }))
  }, [])

  /**
   * Reset dialogue state
   */
  const resetDialogue = useCallback(() => {
    setDialogueState(initialDialogueState)
    setInputModeState('text')
  }, [])

  const value = useMemo<ConversationContextValue>(() => ({
    dialogueState,
    inputMode,
    setInputMode,
    updateTopicCoverage,
    setDialogueComplete,
    resetDialogue,
  }), [dialogueState, inputMode, setInputMode, updateTopicCoverage, setDialogueComplete, resetDialogue])

  return (
    <ConversationContext.Provider value={value}>
      {children}
    </ConversationContext.Provider>
  )
}

/**
 * Hook to use conversation context
 */
export function useConversationContext(): ConversationContextValue {
  const context = useContext(ConversationContext)

  if (!context) {
    throw new Error('useConversationContext must be used within ConversationProvider')
  }

  return context
}

/**
 * Get progress percentage based on required topics covered
 */
export function getProgressPercentage(dialogueState: DialogueState): number {
  return (dialogueState.requiredCovered / dialogueState.requiredTotal) * 100
}

/**
 * Check if a specific topic has been covered
 */
export function isTopicCovered(dialogueState: DialogueState, topic: TopicCategory): boolean {
  return dialogueState.topicsCovered.includes(topic)
}

export default ConversationContext

// Re-export types for convenience
export type QuestionCategory = TopicCategory
